/**
 * useFilterWithLogic Hook
 *
 * Shared logic for multi-select filters with AND/OR toggle.
 * Used by DimensionFilter.
 */

import { useCallback, useMemo } from 'react';
import type { FilterLogic, FilterWithLogic } from '../types/filter.types';

export interface UseFilterWithLogicOptions<T extends string> {
  /** Current filter value */
  value: FilterWithLogic<T> | undefined;
  /** Callback when filter changes */
  onChange: (value: FilterWithLogic<T> | undefined) => void;
  /** Default logic mode for this filter type */
  defaultLogic: FilterLogic;
  /** Noun for helper text (e.g., "dimensions") */
  noun: string;
}

export interface UseFilterWithLogicResult<T extends string> {
  /** Current logic mode (from value or default) */
  currentLogic: FilterLogic;
  /** Handler for when selected values change */
  handleValuesChange: (values: T[] | undefined) => void;
  /** Handler for when logic mode toggles */
  handleLogicChange: (logic: FilterLogic) => void;
  /** Dynamic helper text based on current logic */
  helperText: string;
}

/**
 * Hook for managing filter state with configurable AND/OR logic.
 *
 * @example
 * const { currentLogic, handleValuesChange, handleLogicChange, helperText } = useFilterWithLogic({
 *   value,
 *   onChange,
 *   defaultLogic: 'or',
 *   noun: 'dimensions',
 * });
 */
export function useFilterWithLogic<T extends string>({
  value,
  onChange,
  defaultLogic,
  noun,
}: UseFilterWithLogicOptions<T>): UseFilterWithLogicResult<T> {
  const currentLogic = value?.logic ?? defaultLogic;

  const handleValuesChange = useCallback(
    (values: T[] | undefined) => {
      if (!values || values.length === 0) {
        onChange(undefined);
      } else {
        onChange({
          values,
          logic: currentLogic,
        });
      }
    },
    [onChange, currentLogic]
  );

  const handleLogicChange = useCallback(
    (logic: FilterLogic) => {
      if (value?.values?.length) {
        onChange({ ...value, logic });
      }
    },
    [value, onChange]
  );

  const helperText = useMemo(() => {
    return currentLogic === 'and'
      ? `Select ${noun} (must match all)`
      : `Select ${noun} (matches any)`;
  }, [currentLogic, noun]);

  return {
    currentLogic,
    handleValuesChange,
    handleLogicChange,
    helperText,
  };
}
